import { Map as MaplibreMap, StyleSpecification } from 'maplibre-gl'
import { useEffect, useRef } from 'react'
import { useViewport } from 'reactflow'
import { GeoAnchor, calculateMapZoom, pixelToLatLng } from '../../utils/geo-transform'

interface Props {
  geoAnchor: GeoAnchor
}

const OSM_STYLE: StyleSpecification = {
  version: 8,
  sources: {
    osm: {
      type: 'raster',
      tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'],
      tileSize: 256,
      maxzoom: 19,
      attribution:

        '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    },
  },
  layers: [{ id: 'osm', type: 'raster', source: 'osm' }],
}

export const MapBackground = ({ geoAnchor }: Props) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const mapRef = useRef<MaplibreMap | null>(null)
  const { x, y, zoom } = useViewport()

  // Initialize MapLibre once on mount
  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const map = new MaplibreMap({
      container,
      style: OSM_STYLE,
      interactive: false,
      attributionControl: { compact: true },
    })

    mapRef.current = map

    const observer = new ResizeObserver(() => {
      map.resize()
    })
    observer.observe(container)

    return () => {
      observer.disconnect()
      map.remove()
      mapRef.current = null
    }
  }, [])

  // Sync MapLibre viewport with ReactFlow on every pan/zoom
  useEffect(() => {
    const map = mapRef.current
    const container = containerRef.current
    if (!map || !container) return

    const applyCamera = () => {
      const cx = (container.clientWidth / 2 - x) / zoom
      const cy = (container.clientHeight / 2 - y) / zoom
      const center = pixelToLatLng(cx, cy, geoAnchor)
      const mapZoom = calculateMapZoom(zoom, center.lat, geoAnchor.metersPerPixel)
      map.jumpTo({
        center: [center.lng, center.lat],
        zoom: mapZoom,
        bearing: geoAnchor.northRotationDegrees,
      })
    }

    if (map.isStyleLoaded()) {
      applyCamera()
    } else {
      map.once('load', applyCamera)
      return () => { map.off('load', applyCamera) }
    }
  }, [x, y, zoom, geoAnchor])

  return (
    <div
      ref={containerRef}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: -3,
      }}
    />
  )
}
